import { ComputedProperties, ComputedPropertySchema } from '../../ReplicantComputedProperties';
import { GetIndexSchema, IndexSchema, QueryFilters } from '../../ReplicantIndexes';
import SB from '../../SchemaBuilder';
import { getNestedProperties } from '../../utils/IndexingUtils';

type ValidateOK = { validates: true };
type ValidateFail = { validates: false; reason: string };
export type ValidationResult = ValidateOK | ValidateFail;

function validateOK(): ValidateOK {
    return { validates: true };
}
function validateFail(msg: string): ValidateFail {
    return { validates: false, reason: msg };
}

function betweenNumericValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    if (!Array.isArray(obj)) {
        return validateFail('between expects an array of two numbers');
    }
    if (obj.length !== 2) {
        return validateFail('between expected exactly two numbers for the range');
    }
    let schemaErr: null | string = schema.validate(obj[0]);
    if (schemaErr != null) {
        return validateFail(`between failed to validate lowerbound: ${schemaErr}`);
    }
    schemaErr = schema.validate(obj[1]);
    if (schemaErr != null) {
        return validateFail(`between failed to validate upperbound: ${schemaErr}`);
    }
    return validateOK();
}
function existsValidator(obj: any): ValidationResult {
    if (obj === true || obj === false) {
        return validateOK();
    }

    return validateFail('value must be a boolean');
}
function singleNumericValidator(obj: any, schema: ComputedPropertySchema, field: string): ValidationResult {
    const schemaErr: null | string = schema.validate(obj);
    if (schemaErr != null) {
        return validateFail(`${field} failed to validate the argument: ${schemaErr}`);
    }
    return validateOK();
}
function greaterThanValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return singleNumericValidator(obj, schema, 'greaterThan');
}
function lessThanValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return singleNumericValidator(obj, schema, 'lessThan');
}
function lessThanOrEqualValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return singleNumericValidator(obj, schema, 'lessThanOrEqual');
}
function greaterThanOrEqualValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return singleNumericValidator(obj, schema, 'greaterThanOrEqual');
}
function containsValidator(obj: any, schema: ComputedPropertySchema, field: string): ValidationResult {
    if (!schema.isArraySchema()) {
        return validateFail(`${field} schema must be of array type`);
    }
    if (!Array.isArray(obj)) {
        return validateFail(`${field} values must be of array type`);
    }
    for (const item of obj) {
        const schemaErr = schema.getItemSchema().validate(item);
        if (schemaErr != null) {
            return validateFail(`${field} schema could not validate value: ${schemaErr}`);
        }
    }
    return validateOK();
}
function containsAnyOfValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return containsValidator(obj, schema, 'containsAnyOf');
}
function containsNoneOfValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return containsValidator(obj, schema, 'containsNoneOf');
}

function stringArrayValidator(obj: any, schema: ComputedPropertySchema, field: string): ValidationResult {
    if (!Array.isArray(obj)) {
        return validateFail(`${field} values must be of array type`);
    }

    for (const item of obj) {
        const schemaErr = schema.validate(item);
        if (schemaErr != null) {
            return validateFail(`${field} schema could not validate value: ${schemaErr}`);
        }
    }

    return validateOK();
}

function booleanValidator(obj: any, schema: ComputedPropertySchema, field: string): ValidationResult {
    const schemaErr: null | string = schema.validate(obj);
    if (schemaErr != null) {
        return validateFail(`${field} failed to validate the argument: ${schemaErr}`);
    }
    return validateOK();
}

function isAnyOfValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return stringArrayValidator(obj, schema, 'isAnyOf');
}

function isNoneOfValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return stringArrayValidator(obj, schema, 'isNoneOf');
}

function isValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return booleanValidator(obj, schema, 'isEquals');
}

function isNotValidator(obj: any, schema: ComputedPropertySchema): ValidationResult {
    return booleanValidator(obj, schema, 'isNotEquals');
}

function nonEmptyStringArrayValidatorValidator(obj: any, field: string): ValidationResult {
    if (!Array.isArray(obj) || obj.length === 0) {
        return validateFail(`${field} value must be a non-empty array`);
    }

    const stringSchema = SB.string().customValidator((val) => (val.length > 0 ? null : 'String must be non-empty'));

    for (const item of obj) {
        const schemaErr = stringSchema.validate(item);
        if (schemaErr != null) {
            return validateFail(`Invalid item in ${field} array: ${schemaErr}`);
        }
    }

    return validateOK();
}

function matchesAnyOfValidator(obj: any): ValidationResult {
    return nonEmptyStringArrayValidatorValidator(obj, 'matchesAnyOf');
}

const filterValidators: Record<string, (obj: any, schema: ComputedPropertySchema) => ValidationResult> = {
    between: betweenNumericValidator,
    exists: existsValidator,
    greaterThan: greaterThanValidator,
    greaterThanOrEqual: greaterThanOrEqualValidator,
    lessThan: lessThanValidator,
    lessThanOrEqual: lessThanOrEqualValidator,
    containsAnyOf: containsAnyOfValidator,
    containsNoneOf: containsNoneOfValidator,
    isAnyOf: isAnyOfValidator,
    isNoneOf: isNoneOfValidator,
    isNot: isNotValidator,
    is: isValidator,
    matchesAnyOf: matchesAnyOfValidator,
};

export function validateSearch(searchFields: { [k: string]: ComputedPropertySchema }, obj: any): ValidationResult {
    const result = validate(searchFields, obj);
    if (!result.validates) {
        return result;
    }

    if (!('limit' in obj)) {
        return validateFail('limit must be defined when searching');
    }

    if (typeof obj.limit !== 'number' || !Number.isInteger(obj.limit) || obj.limit < 0 || obj.limit > 10_000) {
        return validateFail('limit must be an integer between [0, 10_000]');
    }

    return validateOK();
}

export function tryValidateSearch(searchFields: { [k: string]: ComputedPropertySchema }, obj: any): void {
    const isValid = validateSearch(searchFields, obj);
    if (!isValid.validates) {
        throw Error(`Unable to validate query: ${isValid.reason}`);
    }
}

export function validateCount(searchFields: { [k: string]: ComputedPropertySchema }, obj: any): ValidationResult {
    const result = validate(searchFields, obj);
    if (!result.validates) {
        return result;
    }

    if ('limit' in obj) {
        return validateFail('limit must not be defined when counting');
    }

    return validateOK();
}

export function tryValidateCount(searchFields: { [k: string]: ComputedPropertySchema }, obj: any): void {
    const isValid = validateCount(searchFields, obj);
    if (!isValid.validates) {
        throw Error(`Unable to validate query: ${isValid.reason}`);
    }
}

export function validate(searchFields: { [k: string]: ComputedPropertySchema }, obj: any, depth = 0): ValidationResult {
    if (obj === null) {
        return validateFail('query object must not be null');
    }

    if (depth > 1) {
        return validateFail('deeply nested queries are not yet supported');
    }

    if (obj.scoring && obj.sort) {
        return validateFail('sort cannot be used when using scoring');
    }

    if (obj.sort && obj.sort !== 'random') {
        if (!Array.isArray(obj.sort)) {
            return validateFail('Sort for the query must either be "random" or an array[]');
        }

        for (const sort of obj.sort) {
            if (sort === 'random') {
                continue;
            }

            // Validate sort order.
            if (!sort.order) {
                return validateFail('sort missing order. Either "asc" or "desc"');
            }
            if (sort.order !== 'asc' && sort.order !== 'desc') {
                return validateFail('sort order must be either "asc" or "desc"');
            }

            // Validate script sort.
            if (sort.script) {
                if (typeof sort.script !== 'function') {
                    return validateFail('sort.script must be a function');
                }

                continue;
            }

            if (!sort.field) {
                return validateFail('sort item missing field property');
            }
            if (typeof sort.field !== 'string') {
                return validateFail('sort field must be a string');
            }

            // Validate sort field.
            const sortFieldSegments = sort.field.split('.');

            // Check for a flattened object field.
            if (sortFieldSegments.length > 1) {
                const flatObjectField = sortFieldSegments.join('>');
                if (flatObjectField in searchFields) {
                    continue;
                }
            }

            let fields = searchFields;

            for (let i = 0; i < sortFieldSegments.length; i++) {
                const segment = sortFieldSegments[i];
                if (!(segment in fields)) {
                    return validateFail('sort field not found in computed properties: ' + sort.field);
                }

                // Drill down the nested fields.
                const schema = fields[segment]!;
                fields = getNestedProperties(schema)!;

                if (i < sortFieldSegments.length - 1) {
                    if (!fields) {
                        return validateFail('sort field invalid: ' + segment + ' is not a nested field.');
                    }
                } else if (fields) {
                    return validateFail('sort field invalid: ' + segment + ' is a nested field.');
                }
            }
        }
    }

    if (!obj.where) {
        return validateFail('First field of the query should be "where"');
    }

    const queries = Array.isArray(obj.where) ? obj.where : [obj.where];

    let numQueries = 0;
    for (const query of queries) {
        let numLabels = 0;
        for (const label in query) {
            if (label === 'id' && depth === 0) {
                if (!query.id.isOneOf && !query.id.isNotOneOf) {
                    return validateFail('id must be filtered with either isOneOf or isNotOneOf');
                }
                if (Object.keys(query.id).some((x) => x !== 'isOneOf' && x !== 'isNotOneOf')) {
                    return validateFail('id can only be filtered with either isOneOf or isNotOneOf');
                }
                if (query.id.isOneOf) {
                    if (!Array.isArray(query.id.isOneOf)) {
                        return validateFail('isOneOf value must be an array type');
                    }
                    if ((query.id.isOneOf as any[]).some((x) => typeof x !== 'string')) {
                        return validateFail('id filters can only be strings');
                    }
                }
                if (query.id.isNotOneOf) {
                    if (!Array.isArray(query.id.isNotOneOf)) {
                        return validateFail('isNotOneOf value must be an array type');
                    }
                    if ((query.id.isNotOneOf as any[]).some((x) => typeof x !== 'string')) {
                        return validateFail('id filters can only be strings');
                    }
                }
            } else {
                if (!(label in searchFields)) {
                    return validateFail(`Field: ${label} not found in computed properties or is not searchable`);
                }
                const schema = searchFields[label as keyof typeof searchFields]!;

                let numFilters = 0;

                // Check if this field is nested.
                const nestedSchema = getNestedProperties(schema);
                if (nestedSchema) {
                    const nestedResult = validate(nestedSchema, { where: query[label] }, ++depth);
                    if (!nestedResult.validates) {
                        return nestedResult;
                    }
                    ++numFilters;
                    ++numLabels;

                    continue;
                }

                for (const filterKey in query[label]) {
                    if (!filterValidators[filterKey]) {
                        return validateFail(`Filter "${filterKey}" not recognized!`);
                    }

                    // TODO validate the other field/filter combinations:
                    if (schema._extendedType === 'matchableString' && filterKey !== 'matchesAnyOf') {
                        return validateFail(
                            `invalid query in where.${label}.${filterKey}, matchable string properties only support matchesAnyOf`,
                        );
                    }

                    const validateResult = filterValidators[filterKey]!(query[label][filterKey], schema);
                    if (!validateResult.validates) {
                        return validateResult;
                    }
                    ++numFilters;
                }
                if (numFilters === 0) {
                    return validateFail('Empty filter block');
                }
            }
            ++numLabels;
        }
        if (numLabels === 0) {
            return validateFail('Empty `where` block in query');
        }
        ++numQueries;
    }

    const scoring = obj.scoring;
    if (scoring) {
        for (const scoringFunctionField in scoring.functions) {
            const decayFn = scoring.functions[scoringFunctionField];
            if (decayFn.scale <= 0) {
                return validateFail(`function for ${scoringFunctionField} scale param must be greater than 0`);
            }
        }
    }

    if (numQueries === 0) {
        return validateFail('Empty');
    }
    return validateOK();
}

type BetweenFN<T> = (field: string, bounds: [number, number]) => T;
type CompareFN<T> = (field: string, val: number) => T;
type EqualityFN<T> = (field: string, val: boolean) => T;
type ExistsFN<T> = (field: string, val: boolean) => T;
type ContainsFN<T, U> = (field: string, values: U[]) => T;
type IntersectFN<T> = (evalResults: T[]) => T;
type UnionFN<T> = (evalResults: T[]) => T;

export type GeneratorStackFrame = {
    fields: { [k: string]: ComputedPropertySchema };
    path: string;
};

type OperatorFunctions<GeneratorOutput> = {
    between: BetweenFN<GeneratorOutput>;
    exists: ExistsFN<GeneratorOutput>;
    lessThan: CompareFN<GeneratorOutput>;
    lessThanOrEqual: CompareFN<GeneratorOutput>;
    greaterThan: CompareFN<GeneratorOutput>;
    greaterThanOrEqual: CompareFN<GeneratorOutput>;
    idOneOf: ContainsFN<GeneratorOutput, string>;
    idNotOneOf: ContainsFN<GeneratorOutput, string>;
    containsAnyOf: ContainsFN<GeneratorOutput, any>;
    containsNoneOf: ContainsFN<GeneratorOutput, any>;
    isAnyOf: ContainsFN<GeneratorOutput, string>;
    isNoneOf: ContainsFN<GeneratorOutput, string>;
    matchesAnyOf: ContainsFN<GeneratorOutput, string>;

    is: EqualityFN<GeneratorOutput>;
    isNot: EqualityFN<GeneratorOutput>;
};

export interface QueryGenerator<GeneratorOutput> extends OperatorFunctions<any> {
    pushStackFrame: (stack: GeneratorStackFrame) => void;
    popStackFrame: () => void;
    searchFields: () => { [k: string]: ComputedPropertySchema };
    intersection: IntersectFN<GeneratorOutput>;
    finalize: (fragment: any) => any;
    union: UnionFN<GeneratorOutput>;
}

export function filterToFunctionName(filter: string): string {
    switch (filter) {
        case 'isOneOf':
            return 'idOneOf';
        case 'isNotOneOf':
            return 'idNotOneOf';
        case 'notOneOf':
            throw new Error('notOneOf is invalid');
        default:
            return filter;
    }
}

function getObjectProperties(schema: ComputedPropertySchema): { [key: string]: ComputedPropertySchema } | null {
    if (schema?.isObjectSchema()) {
        return schema.getFullSchema();
    }
    return null;
}

export function flattenDocument(doc: { [key: string]: any }, parentKey?: string): { [key: string]: any } {
    return Object.entries(doc).reduce(
        (acc, [key, val]) => {
            const fullKey = parentKey ? parentKey + '>' + key : key;
            if (typeof val === 'object' && !Array.isArray(val)) {
                acc = { ...acc, ...flattenDocument(val, fullKey) };
            } else {
                acc[fullKey] = val;
            }

            return acc;
        },
        {} as { [key: string]: any },
    );
}

export function unFlattenDocument(doc: any) {
    const res = Object.entries(doc).reduce((acc: { [key: string]: any }, [key, val]) => {
        const segments = key.split('>');
        let cur: any = acc;
        for (const s of segments.slice(0, segments.length - 1)) {
            if (!cur[s]) cur[s] = {};
            cur = cur[s];
        }
        cur[segments[segments.length - 1]!] = val;

        return acc;
    }, {});

    return res;
}

export function flattenQueryOpts(schema: IndexSchema, opts: QueryFilters<any>): QueryFilters<any> {
    return Object.entries(opts).reduce(
        (acc, [key, value]) => {
            const innerSchema = getObjectProperties(schema[key]!);
            const nestedSchema = getNestedProperties(schema[key]!);
            if (!nestedSchema && innerSchema) {
                const flattened = flattenQueryOpts(innerSchema, value);
                for (const [innerKey, val] of Object.entries(flattened)) {
                    acc[key + '>' + innerKey] = val;
                }
            } else {
                acc[key] = value;
            }
            return acc;
        },
        {} as { [key: string]: any },
    );
}

export function preProcessQuery<
    T extends ComputedProperties,
    TQuery extends { where: QueryFilters<GetIndexSchema<T>> },
>(rawSchema: any, queryOpts: TQuery): TQuery {
    const newQueryOpts = { ...queryOpts };
    newQueryOpts.where = (
        Array.isArray(queryOpts.where)
            ? queryOpts.where.map((w) => flattenQueryOpts(rawSchema, w))
            : [flattenQueryOpts(rawSchema, queryOpts.where)]
    ) as QueryFilters<GetIndexSchema<T>>;

    return newQueryOpts;
}

export function generate<T>(obj: any, generator: QueryGenerator<T>): T {
    const rawFields = generator.searchFields();

    const queries = Array.isArray(obj.where) ? obj.where : [obj.where];
    const unionResults = [];
    for (const query of queries) {
        const intersectionResults = [];
        for (const queryLabel in query) {
            const schema = rawFields[queryLabel as keyof typeof rawFields]!;

            // Handle case if this queryLabel is nested.
            const nestedSchema = getNestedProperties(schema);
            if (nestedSchema) {
                generator.pushStackFrame({ fields: nestedSchema, path: queryLabel });

                const nestedResult = generate({ where: query[queryLabel] }, generator);
                intersectionResults.push(nestedResult);

                generator.popStackFrame();
                continue;
            }

            for (const filter in query[queryLabel]) {
                const filterFunctionName = filterToFunctionName(filter);
                const filterFunction = (generator as any)[filterFunctionName];
                if (!filterFunction) {
                    throw new Error('Invalid filter name ' + filter);
                }
                intersectionResults.push(filterFunction(queryLabel, query[queryLabel][filter]));
            }
        }
        unionResults.push(
            intersectionResults.length > 1 ? generator.intersection(intersectionResults) : intersectionResults[0],
        );
    }

    return generator.finalize(unionResults.length > 1 ? generator.union(unionResults) : unionResults[0]);
}
